// P8b-B1 — Shared planning "taste session" identity.
//
// A single planning INTERACTION spans multiple SPA routes (PlanningHub -> Meals
// -> PlanningHub, GuidedPlanning -> Meals -> GuidedPlanning, PlanningHub ->
// PlanReview). We therefore key weak taste signals to a session id stored in
// sessionStorage (which survives client-side route changes) rather than any
// mutation opId or component-mount id.
//
// Reuse the same id while the LOCAL date is unchanged AND planning has been
// active within the last 60 minutes. Rotate on: no stored session, local-date
// change, or >=60 min of inactivity. Every taste/planning action refreshes
// last_activity_at. This gives route continuity + no same-session stacking, but
// still lets a genuinely later session produce independent evidence (and avoids
// one all-day browser-tab session that suppresses learning).

import { getTodayString } from './dateUtils';

const STORAGE_KEY = 'fitxess.planningTasteSession';
const INACTIVITY_MS = 60 * 60 * 1000; // 60 minutes

function newId() {
  return 'taste_sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

function readStored() {
  try {
    var raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function write(rec) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(rec));
  } catch (e) { /* sessionStorage unavailable — non-fatal for telemetry */ }
}

// Returns the active planning-taste session id, rotating when appropriate, and
// refreshes last_activity_at on every call (each call reflects real activity).
export function getPlanningTasteSessionId() {
  var now = Date.now();
  var today = getTodayString();
  var stored = readStored();

  var reuse = !!(
    stored &&
    stored.id &&
    stored.local_date === today &&
    typeof stored.last_activity_at === 'number' &&
    (now - stored.last_activity_at) < INACTIVITY_MS
  );

  var rec = reuse
    ? { id: stored.id, local_date: today, last_activity_at: now }
    : { id: newId(), local_date: today, last_activity_at: now };

  write(rec);
  return rec.id;
}
